import Button from 'ext-ui/components/Button/Button'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  CONFIRM_DISCONNECT_INTEGRATION_TEXT,
  ConfirmDisconnectIntegrationText,
} from 'src/constants'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import Trns, { useTranslate } from 'src/localization/Trns'
import { supportRequestUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import {
  cancelButtonCss,
  confirmHighlightCss,
  containerCss,
  fieldGroupCss,
  fieldLabelCss,
  footerCss,
  headerLogoCss,
  headerRowCss,
  headerTitleCss,
  headingBlockCss,
  headingSectionCss,
  inputContainerCss,
  inputCss,
  supportLineCss,
  supportLinkCss,
  warningBodyCss,
  warningEmphasisCss,
  warningIconWrapperCss,
  warningPanelCss,
} from './ConfirmDisconnectOrgIntegrationViewV2.style'

interface ConfirmDisconnectOrgIntegrationViewV2Props {
  disconnectIntegration: () => void
  goBack: () => void
  connectedPlatformName: string
  integrationLogo?: React.ReactNode
  loading?: boolean
}

const ConfirmDisconnectOrgIntegrationViewV2: React.FC<
  ConfirmDisconnectOrgIntegrationViewV2Props
> = (props) => {
  const {
    disconnectIntegration,
    goBack,
    connectedPlatformName,
    integrationLogo,
    loading = false,
  } = props

  const locale = useSelector(selectNullSafeLocaleFromState)
  const { EXTERNAL_SUPPORT_URL } = useCurrentBrand()
  const translate = useTranslate()

  const [confirmationText, setConfirmationText] = useState<string>('')

  const requiredText =
    ConfirmDisconnectIntegrationText[locale] ??
    CONFIRM_DISCONNECT_INTEGRATION_TEXT
  const isValid = confirmationText === requiredText
  const supportHref = EXTERNAL_SUPPORT_URL || supportRequestUrl()

  const handleDisconnect = (): void => {
    if (!isValid || loading) return
    disconnectIntegration()
  }

  return (
    <div css={containerCss}>
      <div css={headingSectionCss}>
        <div css={headingBlockCss}>
          <div css={headerRowCss}>
            {integrationLogo !== undefined && integrationLogo !== null && (
              <span
                css={headerLogoCss}
                data-testid="erp-disconnect-integration-logo"
              >
                {integrationLogo}
              </span>
            )}
            <span css={headerTitleCss}>
              {translate('Disconnect from ${connectedPlatform}', {
                connectedPlatform: connectedPlatformName,
              })}
            </span>
          </div>

          <p css={supportLineCss}>
            <Trns
              source={
                'If you need support or assistance troubleshooting your integration please ${contactSupport}'
              }
              expressions={{
                contactSupport: (
                  <a
                    css={supportLinkCss}
                    href={supportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="erp-disconnect-contact-support-link"
                  >
                    {translate('contact support')}
                  </a>
                ),
              }}
            />
          </p>
        </div>

        <div
          css={warningPanelCss}
          data-testid="erp-disconnect-ai-warning-banner"
          role="alert"
        >
          <span css={warningIconWrapperCss} aria-hidden="true">
            <AlertTriangleYellowIcon />
          </span>
          <p css={warningBodyCss}>
            <span css={warningEmphasisCss}>
              {translate('This action may impact your expense workflows.')}
            </span>{' '}
            {translate(
              'Disconnecting ${connectedPlatform} will disable AI coding and category suggestions and stop future expense category syncing.',
              { connectedPlatform: connectedPlatformName },
            )}
          </p>
        </div>
      </div>

      <div css={fieldGroupCss}>
        <span css={fieldLabelCss}>
          <Trns
            source={'Confirm by typing ${confirmString} below:'}
            expressions={{
              confirmString: (
                <span css={confirmHighlightCss}>{`"${requiredText}"`}</span>
              ),
            }}
          />
        </span>
        <div css={inputContainerCss}>
          <input
            css={inputCss}
            type="text"
            name="erp-disconnect-confirmation"
            data-testid="erp-disconnect-confirmation-input"
            autoComplete="off"
            placeholder={translate('Please type "${confirmString}"', {
              confirmString: requiredText,
            })}
            value={confirmationText}
            aria-label={translate('Please type "${confirmString}"', {
              confirmString: requiredText,
            })}
            onChange={(e): void =>
              setConfirmationText(e.target.value.toUpperCase())
            }
          />
        </div>
      </div>

      <div css={footerCss}>
        <button
          type="button"
          css={cancelButtonCss}
          data-testid="erp-disconnect-cancel-button"
          onClick={goBack}
        >
          {translate('Cancel')}
        </button>
        <Button
          variant={'severe'}
          data-testid="erp-disconnect-confirm-button"
          disabled={!isValid}
          loading={loading}
          onClick={handleDisconnect}
        >
          {translate('Disconnect')}
        </Button>
      </div>
    </div>
  )
}

export default ConfirmDisconnectOrgIntegrationViewV2
